import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import { NoCredentialsBox } from 'components/NoCredentialsBox/NoCredentialsBox';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useAuthenticationValidationContext } from '../components/Authentication/context/AuthenticationValidationContext';
import { DeviceCredentials } from '../components/DeviceCredentials/DeviceCredentials';
import type { DeviceCredential } from '../components/DeviceCredentials/DevicePasswordsList';
import type { DiscoverySettingsObject } from '../model/DiscoverySettingsObject';

export type DeviceCredentialsFieldValues = {
  credentials: DeviceCredential[];
};
export type DeviceCredentialsOutputValues = {
  credentials: DeviceCredential[];
};

const getNoCredentialBox = (
  authenticationValidation: ReturnType<
    typeof useAuthenticationValidationContext
  >,
) =>
  authenticationValidation.hasError ? (
    <NoCredentialsBox
      title='No stored credentials'
      icon='fa-exclamation-circle'
      type='error'
      className='-mx-2 -mt-2 border-t-0'
    >
      At least one Device credential or{' '}
      <Link
        renderAs='button'
        onClick={() => {
          authenticationValidation.setSubTab('vendors');
        }}
      >
        Vendor API
      </Link>{' '}
      is required to run a discovery.
    </NoCredentialsBox>
  ) : (
    <NoCredentialsBox
      title='No stored credentials'
      icon='fa-info-circle'
      type='info'
      className='-mx-2 -mt-2 border-t-0'
    />
  );

export const renderTitleBadge = (
  authenticationValidation: ReturnType<
    typeof useAuthenticationValidationContext
  >,
) => {
  if (authenticationValidation.hasError) {
    return (
      <Tooltip content={authenticationValidation.errorLong}>
        <Badge
          variant='plain'
          color='error'
          icon='fa-circle-xmark'
          className='ml-4 cursor-default'
        >
          {authenticationValidation.errorShort}
        </Badge>
      </Tooltip>
    );
  }
  if (authenticationValidation.hasWarning) {
    return (
      <Tooltip content={authenticationValidation.warningLong}>
        <Badge
          variant='plain'
          color='warning'
          icon='fa-circle-exclamation'
          className='ml-4 cursor-default'
        >
          {authenticationValidation.warningShort}
        </Badge>
      </Tooltip>
    );
  }
  return undefined;
};

const DeviceCredentialsFormAdapter = (props: {
  form: UseFormReturn<
    DeviceCredentialsFieldValues,
    unknown,
    DeviceCredentialsOutputValues
  >;
}) => {
  const credentials = useWatch({
    control: props.form.control,
    name: 'credentials',
  });

  const authenticationValidation = useAuthenticationValidationContext();
  const noCredentialBox = getNoCredentialBox(authenticationValidation);

  return (
    <DeviceCredentials
      credentials={credentials}
      onCredentialsChange={(credentials) => {
        props.form.setValue('credentials', credentials, { shouldDirty: true });
      }}
      noCredentialBox={noCredentialBox}
      renderTitleBadge={() => renderTitleBadge(authenticationValidation)}
    />
  );
};

export const storedDeviceCredentials: FormSnippet<
  DiscoverySettingsObject,
  DeviceCredentialsFieldValues,
  DeviceCredentialsOutputValues
> = {
  render: ({ form }) => <DeviceCredentialsFormAdapter form={form} />,

  fromModel: (model, fieldValuesDraft) => {
    // normalize and copy incoming credentials so they match DeviceCredential[]
    fieldValuesDraft.credentials = model.credentials ?? [];
  },

  toModel: (transformedValues, modelDraft) => {
    modelDraft.credentials = transformedValues.credentials;
  },

  schema: z.object({
    credentials: z.any(),
  }) as z.Schema<{ credentials: DeviceCredential[] }>,
};
